import React from 'react';
import { Link } from 'react-router-dom';
import { Target, BrainCircuit, Wrench, ArrowRight, CheckCircle2 } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/Reveal';

const VALUES = [
  { icon: Target, title: 'Singular Focus', text: 'We work only in education. That singularity means less distraction and sharper, more relevant advice for your business.' },
  { icon: BrainCircuit, title: 'Insider Knowledge', text: 'Our principals are firmly embedded within the UAE education sector, with access to a wide range of data and real-world specialists.' },
  { icon: Wrench, title: 'Practical Outcomes', text: 'The data and insight we deliver is practical, not theoretical — designed to drive positive, real-world change.' },
];

export default function About() {
  return (
    <div>
      <PageBanner
        title="About Us"
        subtitle="Experienced educators and consultants with an insider’s view of UAE education."
        image="https://images.unsplash.com/photo-1651467606797-e1c660cf3fda?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxEdWJhaSUyMHNreWxpbmV8ZW58MHx8fHwxNzkwODQ5MjY0fDA&ixlib=rb-4.1.0&q=85"
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="text-[#1ca5dc] font-heading font-semibold uppercase tracking-widest text-sm">Who we are</span>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#132b4d] mt-3 mb-6">
              A consultancy built around the UAE education sector
            </h2>
            <p className="text-gray-600 leading-relaxed mb-5">
              The Education Intelligence Group (EIG) is the schools and investor business of Which Media. We bring
              together highly experienced educators and consultants who have a detailed, on-the-ground understanding of
              the real issues affecting education in the United Arab Emirates today.
            </p>
            <p className="text-gray-600 leading-relaxed mb-5">
              On a daily basis we work at the highest level with school operators, investors, regulators, principals
              and senior management. That combination of diverse, complementary experience across curricula — allied
              to rigorous analysis of data — lets us give investors and industry leaders a genuinely unique
              perspective on the market.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We are part of a larger group with access to multiple sources of data, including the leading UAE
              education websites WhichSchoolAdvisor.com and SchoolsCompared.com.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <img
              src="https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGNvbnN1bHRpbmd8ZW58MHx8fHwxNzkwODQ5MjY0fDA&ixlib=rb-4.1.0&q=85"
              alt="The EIG team"
              className="rounded-2xl shadow-xl w-full h-[460px] object-cover"
            />
          </Reveal>
        </div>
      </section>

      <section className="py-20 eig-graph">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center">
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#132b4d] eig-section-title">What we stand for</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 100}>
                <div className="bg-white rounded-xl p-8 h-full shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <div className="w-14 h-14 rounded-lg bg-[#eaf6fc] text-[#1ca5dc] flex items-center justify-center mb-6">
                    <v.icon size={26} />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-[#132b4d] mb-3">{v.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="bg-[#0f2340] rounded-2xl p-10 md:p-14 text-center">
              <h3 className="text-white font-heading font-extrabold text-2xl md:text-3xl mb-4">
                Let’s talk about your challenge
              </h3>
              <p className="text-gray-300 max-w-2xl mx-auto mb-8">
                Whether you are an established school, an operator or an investor exploring the UAE, we would be glad
                to put together a needs-based plan that is fit for purpose.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/contact" className="inline-flex items-center gap-2 bg-[#1ca5dc] text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-white hover:text-[#132b4d] transition-colors">
                  Get in touch <ArrowRight size={18} />
                </Link>
                <Link to="/reports" className="inline-flex items-center gap-2 border border-white/40 text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors">
                  Browse reports
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
